<template lang="pug">
div
  ErrorAlert(
    v-if="error"
    :message="error.message"
    :stack="error.stack"
    @report="() => report(error)"
  )
  CommentTeaserCard.my-2(
    v-for="(comment, key) in comments"
    v-else
    :key="key"
    :heading="comment.heading"
    :teaser="comment.teaser"
    :ranges="comment.ranges"
    :detail-level-name="comment.detailLevel.name[locale] ?? '?'"
    :detail-level-num-dots="numDotsPerDetailLevelWeight[comment.detailLevel.weight] ?? 0"
    :nid="comment.nid"
    :local-tid="localTid"
    :commentary-type-machine-name="comment.commentaryType.machineName"
    :show-default-reading-list="true"
    :show-reading-list-menu="true"
    :language="comment.language"
  )
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { computed } from "vue";
import { createVidRange, type VidRangeData } from "~/units/scripture";
import { injectRequired } from "~/injectRequired";
import { LogFunctionKey, SolrClientKey } from "~/injectionKeys";
import {
  getDetailLevelFromWeightWithFallback,
  numDotsPerDetailLevelWeight,
} from "~/static/taxonomy/detailLevels";
import { getCommentaryTypeOrFallback } from "~/static/taxonomy/commentaryTypes";
import { parseJsonItemsAndSkipFailures } from "~/units/json";
import { useReport } from "~/logging/report";

const report = useReport();

const i18n = useI18n();
const locale = i18n.locale;
const solrClient = injectRequired(SolrClientKey);
const log = injectRequired(LogFunctionKey);

interface Props {
  /**
   * Comment languages variations
   */
  languages: string[];

  /**
   * The ID of the comment for which a list of similar content will be shown.
   */
  nid: number;

  /**
   * The ID of the user's preferred local translation.
   */
  localTid: string;
}

const props = defineProps<Props>();

/**
 * Search results from Solr.
 */
const query = useNodes(
  solrClient,
  computed(() => [props.nid]),
  computed(() => props.languages),
  [
    "its_nid",
    "ss_heading",
    "zs_body_teaser",
    "zm_scripture",
    "its_detail_weight",
    "sm_type_machine_name",
    "ss_search_api_language",
  ],
  "commentary_comments",
  log,
);

const comments = computed(() => {
  return (query.data.value?.value ?? []).map((doc) => {
    return {
      nid: doc.its_nid,
      heading: doc.ss_heading,
      teaser: doc.zs_body_teaser,
      ranges: parseJsonItemsAndSkipFailures<VidRangeData>(
        doc.zm_scripture ?? [],
      ).map(createVidRange),
      detailLevel: getDetailLevelFromWeightWithFallback(
        doc.its_detail_weight ?? 0,
      ),
      commentaryType: getCommentaryTypeOrFallback(
        doc.sm_type_machine_name?.[0] || "",
      ),
      language: doc.ss_search_api_language,
    };
  });
});

const error = computed(() => query.error.value ?? query.data.value?.error);

onServerPrefetch(async () => {
  await query.suspense();
});
</script>
